import { useApolloClient } from '@apollo/client'
import type { AvailableIntegrationsQuery } from 'generated/resources/graphql'
import {
  IntegrationsSyncableStateFieldsFragmentDoc,
  SyncableStateUpdatedDocument,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'

type MarketplaceDbIntegration =
  AvailableIntegrationsQuery['integrations']['availableIntegrationsConnection']['nodes'][number]['marketplaceIntegration']

interface UseSyncableStateUpdatedSubscriptionProps {
  marketplaceIntegration: MarketplaceDbIntegration | null
  syncEnabled: boolean
}

export const useSyncableStateUpdatedSubscription = ({
  marketplaceIntegration,
  syncEnabled,
}: UseSyncableStateUpdatedSubscriptionProps) => {
  const client = useApolloClient()

  useSubscription(SyncableStateUpdatedDocument, {
    onData: ({ data: subscriptionData }) => {
      const syncableState = subscriptionData.data?.integrationsSyncableStateUpdate?.syncableState
      if (!syncableState || !marketplaceIntegration) return
      // `IntegrationsSyncableState` is a normalized entity (keyed by `id`).
      // Updating it updates every consumer that references it, without coupling
      // this handler to domain-specific "syncable" types.
      const syncableStateCacheId = client.cache.identify({
        __typename: 'IntegrationsSyncableState',
        id: syncableState.id,
      })
      if (!syncableStateCacheId) return
      client.writeFragment({
        id: syncableStateCacheId,
        fragment: IntegrationsSyncableStateFieldsFragmentDoc,
        fragmentName: 'IntegrationsSyncableStateFields',
        data: {
          id: syncableState.id,
          errorMessages: syncableState.errorMessages,
          marketplaceIntegrationId: marketplaceIntegration.id,
          status: syncableState.status,
          statusUpdatedAt: syncableState.statusUpdatedAt,
          syncableId: syncableState.syncableId,
          syncableType: syncableState.syncableType,
          __typename: 'IntegrationsSyncableState',
        },
      })
    },
    skip: !syncEnabled,
  })
}
